import { useState, useEffect, useCallback } from "react";
import { useSnapshot } from "valtio";
import { createUseStyles } from "react-jss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";

import Selector from "./Selector";
import { fetchApi } from "../services/api";
import { currentAccount } from "../proxies";

const EventSelector = ({ className, light, teamMode, tourInfo }) => {
  const classes = useStyles();
  const {
    account,
    accounts,
    activeAccount,
    team,
    teams,
    activeTeam,
    event,
    events,
  } = useSnapshot(currentAccount);
  const [loading, setLoading] = useState({
    accounts: accounts.length === 0,
    teams: teams.length === 0,
    events: events.length === 0,
  });
  const [logos, setLogos] = useState({});
  const [expandedMenus, setExpandedMenus] = useState([false, false, false]);

  const fetchAccounts = useCallback(() => {
    setLoading(l => ({ ...l, accounts: true }));
    currentAccount.accounts = [];

    fetchApi("/accounts/list").then(accounts => {
      const logos = {};
      for (const account of accounts) {
        logos[account.id] = account.logo;
      }

      currentAccount.accounts = accounts.map(account => ({
        label: account.name,
        value: account.id,
      }));

      setLoading(l => ({ ...l, accounts: false }));
      setLogos(logos);

      // Default account value
      let newAccount = accounts[0].id;

      // Check if we have a latest account, and if so, we should try select it
      const tourAccount = tourInfo.account;

      if (tourAccount !== null) {
        // Check if this account exists
        const foundAccount = accounts.find(item => item.id === tourAccount);
        if (foundAccount !== undefined) {
          newAccount = foundAccount.id;
        }
      }

      currentAccount.account = newAccount;
    });
  }, [tourInfo.account]);

  const fetchTeams = useCallback(() => {
    setLoading(l => ({ ...l, teams: true }));
    currentAccount.teams = [];

    fetchApi(`/teams/list/${account}`).then(teams => {
      currentAccount.teams = teams.map(team => ({
        label: team.name,
        value: team.id,
      }));
      setLoading(l => ({ ...l, teams: false }));

      let newTeam = null;

      if (teams.length > 0) {
        newTeam = teams[0].id;
      }

      // Check if we have a latest team, and if so, we should try select it
      const tourTeam = tourInfo.team;

      if (tourTeam !== null) {
        // Check if this team exists
        if (teams.find(item => item.id === tourTeam) !== undefined) {
          newTeam = tourTeam;
        }
      }

      if (newTeam === null) {
        if (teams.findIndex(item => item.id === team) > -1) {
          newTeam = team;
        }
      }

      currentAccount.team = newTeam;
      currentAccount.activeAccount = account;
    });
  }, [account, team, tourInfo.team]);

  const fetchEvents = useCallback(() => {
    setLoading(l => ({ ...l, events: false }));
    currentAccount.events = [];

    fetchApi(`/events/list/${team}`).then(events => {
      currentAccount.events = events.map(event => ({
        label: event.name,
        value: event.id,
      }));
      setLoading(l => ({ ...l, events: false }));

      let newEvent = null;

      // Check if we have a latest event, and if so, we should try select it
      const latestEvent = window.localStorage.getItem("latestEvent");

      if (latestEvent !== null) {
        // Check if this event exists
        const latestEventNum = parseInt(latestEvent, 10);
        if (events.findIndex(item => item.id === latestEventNum) > -1) {
          newEvent = latestEventNum;
        }
      }

      if (newEvent === null) {
        if (
          event !== null &&
          events.findIndex(item => item.id === event) > -1
        ) {
          newEvent = event;
        } else if (events.length > 0) {
          newEvent = events[0].id;
        }
      }

      currentAccount.event = newEvent;
      currentAccount.activeTeam = team;
    });
  }, [event, team]);

  useEffect(() => {
    if (account === null) {
      fetchAccounts();
    }
  }, [account, fetchAccounts]);

  useEffect(() => {
    if (account !== activeAccount && account) {
      fetchTeams();
    }
  }, [account, activeAccount, fetchTeams]);

  useEffect(() => {
    if (team !== activeTeam && team) {
      fetchEvents();
    }
  }, [activeTeam, fetchEvents, team]);

  const updateAccount = account => {
    currentAccount.account = account;
    currentAccount.team = null;
    currentAccount.event = null;
  };

  const updateTeam = team => {
    currentAccount.team = team;
    currentAccount.event = null;
  };

  const updateEvent = event => {
    currentAccount.event = event;
  };

  const toggleMenu = ind => {
    return () => {
      setExpandedMenus(menus =>
        menus.map((val, i) => (i === ind ? !val : false)),
      );
    };
  };

  const renderLogo = () => {
    const logo = logos[account];

    if (logo === null || logo === undefined || logo.length === 0) {
      return (
        <FontAwesomeIcon
          icon={["fal", "building"]}
          className={clsx(classes.icon, classes.accounts)}
          fixedWidth
        />
      );
    }

    return (
      <div
        style={{ backgroundImage: `url(${logo})` }}
        className={classes.logo}
      />
    );
  };

  return (
    <div className={className}>
      {accounts.length > 1 && (
        <div className={classes.selectorRow}>
          {account === null && (
            <FontAwesomeIcon
              icon={["fal", "building"]}
              className={classes.icon}
              fixedWidth
            />
          )}
          {account !== null && renderLogo()}
          <Selector
            options={accounts}
            loading={loading.accounts}
            value={account}
            onChange={updateAccount}
            light={light}
            expanded={expandedMenus[0]}
            toggle={toggleMenu(0)}
            isTeamSelect
          />
        </div>
      )}
      <div className={classes.selectorRow}>
        <FontAwesomeIcon
          icon={["fal", "users"]}
          className={classes.icon}
          fixedWidth
        />
        <Selector
          options={teams}
          loading={loading.accounts || loading.teams}
          value={team}
          onChange={updateTeam}
          light={light}
          expanded={expandedMenus[1]}
          toggle={toggleMenu(1)}
          isTeamSelect
        />
      </div>
      {teamMode !== true && (
        <div className={classes.selectorRow}>
          <FontAwesomeIcon
            icon={["fal", "camera"]}
            className={classes.icon}
            fixedWidth
          />
          <Selector
            options={events}
            loading={loading.accounts || loading.teams || loading.events}
            value={event}
            onChange={updateEvent}
            emptyPlaceholder="No events found."
            light={light}
            expanded={expandedMenus[2]}
            toggle={toggleMenu(2)}
            isTeamSelect
          />
        </div>
      )}
    </div>
  );
};

const useStyles = createUseStyles(theme => ({
  selectorRow: {
    display: "flex",
    flex: 1,
    marginBottom: theme.spacing2 + 2,
    position: "relative",
    height: 36,
  },
  logo: {
    backgroundSize: "contain",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center",
    width: "22.5px",
    height: 29,
    margin: [0, theme.spacing2, 0],
  },
  icon: {
    width: "22.5px",
    fontSize: theme.iconSize,
    margin: [6, theme.spacing2, 0],
    color: "white",
  },
}));

export default EventSelector;
